import { Component, OnInit, OnDestroy } from "@angular/core";
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    NavigationEnd,
    Router,
} from "@angular/router";
import { Observable, Subject } from "rxjs";
import {
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    takeUntil,
} from "rxjs/operators";
import { BreakpointObserver } from "@angular/cdk/layout";
import { AssessmentService, UserService } from "@/_services";
import { OnboardingGuideService } from "@/_services/onboarding-guide.service";
import { RiseAgentService } from "@/_services/rise-agent.service";
import { DismissedBanner, UserDm } from "@/_models";
import UserModel = UserDm.UserModel;

const CONTEXTUAL_NAV_PREFIXES = [
    "/performance",
    "/assurance",
    "/risk-management",
    "/policies",
    "/manuals",
    "/profile",
    "/promotions",
];

// Matches /assessments/[24-char hex MongoDB ObjectId] and sub-paths.
// Excludes /assessments (list) and /assessments/new (templates).
const ASSESSMENT_CONTEXTUAL_RE = /^\/assessments\/[a-f0-9]{24}(\/|$)/i;

@Component({
    selector: "app-pages",
    templateUrl: "./pages.component.html",
    styleUrls: ["./pages.component.scss"],
})
export class PagesComponent implements OnInit, OnDestroy {
    hideMenu$: Observable<{
        hideDesktopMenu: boolean;
        hideMobileMenu: boolean;
    }>;
    showContextualBar$: Observable<boolean>;
    showMobileNavChrome$: Observable<boolean>;
    isDesktopLayout$!: Observable<boolean>;
    hasNotDismissedBanner$: Observable<boolean>;
    asapFormId$: Observable<string | null>;

    riseAgentVisible = false;
    displayMode: "overlay" | "pinned" = "overlay";

    private destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private assessmentService: AssessmentService,
        private onboardingGuideService: OnboardingGuideService,
        private userService: UserService,
        private riseAgentService: RiseAgentService,
        private breakpointObserver: BreakpointObserver,
    ) {}

    ngOnInit() {
        this.isDesktopLayout$ = this.breakpointObserver
            .observe("(min-width: 768px)")
            .pipe(
                map((state) => state.matches),
                distinctUntilChanged(),
                shareReplay(1),
            );

        this.hideMenu$ = this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            startWith(this.router),
            map(() =>
                this.childrenCheck(this.activatedRoute.snapshot.firstChild),
            ),
            shareReplay(),
        );

        this.showMobileNavChrome$ = this.hideMenu$.pipe(
            map((h) => !h?.hideMobileMenu),
            shareReplay(1),
        );

        this.showContextualBar$ = this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            startWith(null),
            map(() => {
                const path = this.router.url.split("?")[0];
                return (
                    CONTEXTUAL_NAV_PREFIXES.some((prefix) =>
                        path.startsWith(prefix),
                    ) || ASSESSMENT_CONTEXTUAL_RE.test(path)
                );
            }),
            shareReplay(),
        );

        const currentUser$ = this.userService
            .getCurrentUserAsync()
            .pipe(shareReplay());

        this.hasNotDismissedBanner$ = currentUser$.pipe(
            map((user: UserModel) => {
                const dismissed = user?.dismissedBanners || [];
                return !dismissed.includes(DismissedBanner.OnboardingGuide);
            }),
            shareReplay(1),
            takeUntil(this.destroy$),
        );

        this.asapFormId$ = this.onboardingGuideService.asapFormId$;

        this.riseAgentService.visibility$
            .pipe(takeUntil(this.destroy$))
            .subscribe((visible) => (this.riseAgentVisible = visible));

        this.riseAgentService.displayMode$
            .pipe(takeUntil(this.destroy$))
            .subscribe((mode) => (this.displayMode = mode));

        this.loadOnboardingStatus();
    }

    openRiseAgent(): void {
        this.riseAgentService.open();
    }

    onRiseAgentVisibleChange(visible: boolean): void {
        if (!visible) {
            this.riseAgentService.close();
        }
    }

    private childrenCheck(snapshot: ActivatedRouteSnapshot): {
        hideDesktopMenu: boolean;
        hideMobileMenu: boolean;
    } {
        if (
            snapshot.data.hasOwnProperty("hideDesktopMenu") ||
            snapshot.data.hasOwnProperty("hideMobileMenu")
        ) {
            return {
                hideDesktopMenu: snapshot.data["hideDesktopMenu"] || false,
                hideMobileMenu: snapshot.data["hideMobileMenu"] || false,
            };
        }

        if (snapshot.children.length) {
            return this.childrenCheck(snapshot.firstChild);
        }

        return { hideDesktopMenu: false, hideMobileMenu: false };
    }

    private loadOnboardingStatus() {
        this.assessmentService
            .listAssessments()
            .pipe(
                map((assessments) =>
                    assessments.find(
                        (assessment) => assessment.title === "ASAP Form",
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((asapForm) => {
                if (asapForm) {
                    this.onboardingGuideService.setAsapFormId(asapForm._id);
                    this.onboardingGuideService
                        .refresh(asapForm._id)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe();
                } else {
                    this.onboardingGuideService
                        .refresh()
                        .pipe(takeUntil(this.destroy$))
                        .subscribe();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
